<template>
  <v-card class="glass-card mt-2">
    <!-- Gradient header -->
    <div class="gradient-header pa-4 d-flex align-center">
      <v-icon color="white" class="mr-3">mdi-history</v-icon>
      <span class="white--text text-h6 font-weight-medium">Povijest ispravaka</span>
      <v-spacer />
      <v-btn icon dark small :loading="loading" @click="loadHistory">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="corrections"
      :loading="loading"
      :expanded.sync="expanded"
      item-key="id"
      show-expand
      :items-per-page="25"
      dense
      class="glass-table"
    >
      <template v-slot:item.created_at="{ item }">
        {{ formatDate(item.created_at) }}
      </template>

      <template v-slot:item.delta="{ item }">
        <span :class="item.delta >= 0 ? 'success--text font-weight-bold' : 'error--text font-weight-bold'">
          {{ item.delta >= 0 ? '+' : '' }}{{ item.delta }}
        </span>
      </template>

      <template v-slot:item.period="{ item }">
        {{ formatDate(item.date_from) }} – {{ formatDate(item.date_to) }}
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" dark class="status-chip">
          {{ statusLabel(item.status) }}
        </v-chip>
      </template>

      <template v-slot:item.progress="{ item }">
        <span class="caption">{{ item.processed_items || 0 }} / {{ item.total_items || '?' }}</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          v-if="item.status === 'completed' && !item.is_revert"
          x-small
          elevation="0"
          class="revert-btn mr-1"
          :loading="actionLoading[item.id] === 'revert'"
          @click="revert(item)"
        >
          Povrat
        </v-btn>
        <v-btn
          v-if="item.status === 'in_progress' || item.status === 'failed'"
          x-small
          elevation="0"
          class="retry-btn"
          :loading="actionLoading[item.id] === 'retry'"
          @click="retry(item)"
        >
          Nastavi
        </v-btn>
      </template>

      <template v-slot:expanded-item="{ headers: hdrs, item }">
        <td :colspan="hdrs.length" class="pa-3">
          <v-data-table
            :headers="subHeaders"
            :items="item.subItems || []"
            :loading="item.loadingSubItems"
            dense
            hide-default-footer
            :items-per-page="-1"
            class="sub-table"
          >
            <template v-slot:item.original_quantity="{ item: sub }">
              {{ formatQty(sub.original_quantity) }}
            </template>
            <template v-slot:item.new_quantity="{ item: sub }">
              {{ formatQty(sub.new_quantity) }}
            </template>
            <template v-slot:item.assigned_delta="{ item: sub }">
              {{ formatQty(sub.assigned_delta) }}
            </template>
          </v-data-table>
        </td>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { df } from '@/plugins/firebase'
import axios from 'axios'
import state from '@/state'

export default {
  name: 'PovijestIspravaka',
  props: {
    warehouseId: { type: String, required: true }
  },
  data () {
    return {
      loading: false,
      corrections: [],
      expanded: [],
      actionLoading: {},
      headers: [
        { text: 'Datum', value: 'created_at', width: 110 },
        { text: 'Artikl', value: 'ingredient_name' },
        { text: 'Delta', value: 'delta', width: 90, align: 'right' },
        { text: 'Period', value: 'period', sortable: false },
        { text: 'Status', value: 'status', width: 120 },
        { text: 'Napredak', value: 'progress', width: 90 },
        { text: 'Kreirao', value: 'created_by', width: 120 },
        { text: 'Akcije', value: 'actions', sortable: false, width: 120 },
        { text: '', value: 'data-table-expand' }
      ],
      subHeaders: [
        { text: 'Br. otpremnice', value: 'shipment_note_number' },
        { text: 'Artikl', value: 'product_name' },
        { text: 'Orig. kol.', value: 'original_quantity', align: 'right' },
        { text: 'Nova kol.', value: 'new_quantity', align: 'right' },
        { text: 'Delta', value: 'assigned_delta', align: 'right' },
        { text: 'Status', value: 'status' }
      ]
    }
  },
  created () {
    this.loadHistory()
  },
  watch: {
    expanded (newVal) {
      for (const item of newVal) {
        if (!item.subItems) this.loadSubItems(item)
      }
    }
  },
  methods: {
    async loadHistory () {
      if (!this.warehouseId) return
      this.loading = true
      try {
        const snap = await df
          .collection('normative_corrections')
          .doc(this.warehouseId)
          .collection('corrections')
          .orderBy('created_at', 'desc')
          .limit(50)
          .get()
        this.corrections = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) {
        console.warn('Failed to load corrections history', e)
      } finally {
        this.loading = false
      }
    },

    async loadSubItems (item) {
      this.$set(item, 'loadingSubItems', true)
      try {
        const snap = await df
          .collection('normative_corrections')
          .doc(this.warehouseId)
          .collection('corrections')
          .doc(item.id)
          .collection('items')
          .get()
        this.$set(item, 'subItems', snap.docs.map(d => ({ id: d.id, ...d.data() })))
      } catch (e) {
        this.$set(item, 'subItems', [])
      } finally {
        this.$set(item, 'loadingSubItems', false)
      }
    },

    async revert (item) {
      this.$set(this.actionLoading, item.id, 'revert')
      try {
        const user = state.getUser()
        const userName = user ? `${user.name || ''} ${user.surname || ''}`.trim() : ''
        const apiUrl = process.env.VUE_APP_NORMCORRECTION_API_URL
        await axios.post(`${apiUrl}/revert`, {
          job_id: item.id,
          warehouse_id: this.warehouseId,
          created_by: userName
        })
        await this.loadHistory()
      } catch (e) {
        console.warn('Revert failed', e)
      } finally {
        this.$set(this.actionLoading, item.id, null)
      }
    },

    async retry (item) {
      this.$set(this.actionLoading, item.id, 'retry')
      try {
        const apiUrl = process.env.VUE_APP_NORMCORRECTION_API_URL
        await axios.post(`${apiUrl}/retry`, { job_id: item.id, warehouse_id: this.warehouseId })
        await this.loadHistory()
      } catch (e) {
        console.warn('Retry failed', e)
      } finally {
        this.$set(this.actionLoading, item.id, null)
      }
    },

    formatDate (val) {
      if (!val) return '—'
      let d
      if (val && typeof val.toDate === 'function') {
        d = val.toDate()
      } else if (typeof val === 'number') {
        d = new Date(val * 1000)
      } else {
        d = new Date(val)
      }
      if (isNaN(d.getTime())) return String(val)
      return d.toLocaleDateString('hr-HR')
    },

    statusColor (status) {
      return { completed: 'success', in_progress: 'blue', failed: 'error', reverting: 'orange', reverted: 'grey' }[status] || 'grey'
    },

    formatQty (val) {
      if (val == null) return '—'
      return (val / 1000).toFixed(3)
    },

    statusLabel (status) {
      return { completed: 'Završeno', in_progress: 'U tijeku', failed: 'Greška', reverting: 'Povratak', reverted: 'Poništeno' }[status] || status
    }
  }
}
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.15) !important;
  overflow: hidden;
}

.gradient-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.9) 0%,
    rgba(57, 137, 201, 0.85) 50%,
    rgba(97, 84, 164, 0.9) 100%);
}

.glass-table {
  background: transparent !important;
}

.glass-table >>> thead tr th {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.06) 0%,
    rgba(57, 137, 201, 0.04) 50%,
    rgba(97, 84, 164, 0.06) 100%) !important;
  border-bottom: 2px solid rgba(0, 179, 190, 0.2) !important;
}

.glass-table >>> tbody tr:hover {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.05) 0%,
    rgba(57, 137, 201, 0.04) 100%) !important;
}

.status-chip {
  font-size: 11px !important;
}

.revert-btn {
  background: linear-gradient(135deg,
    rgba(255, 152, 0, 0.9) 0%,
    rgba(255, 193, 7, 0.85) 100%) !important;
  color: white !important;
  border-radius: 8px !important;
}

.retry-btn {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.9) 0%,
    rgba(57, 137, 201, 0.85) 100%) !important;
  color: white !important;
  border-radius: 8px !important;
}

.sub-table {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.03) 0%,
    rgba(97, 84, 164, 0.03) 100%) !important;
  border: 1px solid rgba(0, 179, 190, 0.1) !important;
  border-radius: 8px !important;
}
</style>
